import {
  Avatar,
  AvatarProps,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  StackProps,
  styled,
  useTheme,
} from '@mui/material'
import { notProp } from '@portal/pkg-utils'
import { UserAvatar, UserAvatarProps } from './UserAvatar'
import { userAvatarSizeStyles } from './userAvatarStyles'
import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePopover } from '@portal/pkg-hooks'
import { DisplayableUser } from './model'

export interface UserAvatarGroupProps extends StackProps {
  users: ReadonlyArray<DisplayableUser>
  variant?: 'standard' | 'compressed'
  maxUsers?: number
  size?: UserAvatarProps['size']
  renderAvatar?: (props: ComponentProps<typeof UserAvatar>) => React.ReactNode
}

export const name = 'UserAvatarGroup'

const compressedMarginFor = (size: UserAvatarGroupProps['size'] = 'medium') => {
  switch (size) {
    case 'small':
      return -0.5
    case 'medium':
      return -0.6
    case 'large':
      return -1.5
  }
}

const Root = styled(Stack, {
  name,
  slot: 'Root',
  shouldForwardProp: notProp('variant'),
})<Omit<UserAvatarGroupProps, 'users' | 'maxUsers'>>(({ variant, size, theme: { spacing } }) => ({
  '& *': {
    marginRight: spacing(1),
  },
  ...(variant === 'compressed' && {
    '& *': {
      paddingTop: 1,
      '&:hover': {
        zIndex: 2,
      },
    },
    '& *:not(:first-of-type)': {
      marginLeft: spacing(compressedMarginFor(size)),
    },
  }),
}))

const ExtraAvatar = styled(Avatar, {
  shouldForwardProp: notProp('size'),
})<AvatarProps & { size: UserAvatarProps['size'] }>(({ size = 'medium' }) =>
  userAvatarSizeStyles(size),
)

export const UserAvatarGroup: React.FC<UserAvatarGroupProps> = ({
  users,
  variant = 'standard',
  maxUsers: maxUsersProp,
  size = 'medium',
  renderAvatar = (p) => <UserAvatar {...p} />,
  ...props
}) => {
  const { t } = useTranslation()
  const { spacing } = useTheme()

  const rootRef = useRef<HTMLDivElement>(null)
  const [maxUsers, setMaxUsers] = useState(maxUsersProp)

  useEffect(() => {
    if (!maxUsersProp && rootRef.current?.parentElement) {
      const avatarWidth =
        userAvatarSizeStyles(size).width + (variant !== 'compressed' ? parseInt(spacing(1.5)) : 0)
      const maxUsersCalculated = Math.floor(
        (rootRef.current.parentElement.offsetWidth - 125) / avatarWidth,
      )
      if (maxUsersCalculated >= 0) {
        setMaxUsers(maxUsersCalculated)
      }
    }
  }, [maxUsersProp, rootRef, size, spacing, variant])

  const { handleOpen, PopoverProps } = usePopover()

  const extraCount = useMemo(() => users.length - (maxUsers || users.length), [maxUsers, users])

  const shownUsers = useMemo(() => users.slice(0, maxUsers), [users, maxUsers])
  const extraUsers = useMemo(() => users.slice(maxUsers || 0), [users, maxUsers])

  return (
    <>
      <Root
        ref={rootRef}
        size={size}
        direction="row"
        variant={variant}
        data-testid={`${name}-Root`}
        {...props}
      >
        {shownUsers.map((user) => renderAvatar({ key: user.identifier, size, ...user }))}
        {extraCount > 0 && (
          <ExtraAvatar
            size={size}
            onClick={handleOpen}
            data-testid={`${name}-ExtraAvatar`}
          >{`+${extraCount}`}</ExtraAvatar>
        )}
      </Root>
      {extraCount > 0 && (
        <Popover
          {...PopoverProps}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          data-testid={`${name}-Popover`}
        >
          {extraUsers.map((user) => (
            <ListItem dense key={user.identifier}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <UserAvatar size="small" {...user} />
              </ListItemIcon>
              <ListItemText
                primary={t('displayName', {
                  givenName: user.givenName,
                  familyName: user.familyName,
                })}
              />
            </ListItem>
          ))}
        </Popover>
      )}
    </>
  )
}
